import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClipPlayback, ClipPublic, ClipTag, RuleArticle } from '@proref/types';
import { useAuth } from '../contexts/auth-context';
import { useClipQuery, useRefereeClipsInfiniteQuery } from '../hooks/use-clips';
import { useDailyClipQuery, useStreakQuery } from '../hooks/use-daily';
import { placeholderDim } from '../lib/query-ui';
import { useClipTagsQuery } from '../hooks/use-clip-tags';
import { useInfiniteScroll } from '../hooks/use-infinite-scroll';
import { useRefereeStatsQuery } from '../hooks/use-stats';
import { useRulesQuery, useSportsQuery } from '../hooks/use-sports';
import { tagChipStyle, tagSoftStyle } from '../lib/tag-colors';
import { timeAgo } from '../lib/format';
import { PageHeader } from '../components/shared/PageHeader';
import { Spinner } from '../components/shared/Spinner';
import { Skeleton } from '../components/shared/Skeleton';
import { Button } from '../components/shared/Button';
import { Icon } from '../components/shared/Icons';
import { AnimatedNumber } from '../components/shared/AnimatedNumber';
import { ClipAnswerPanel } from '../components/clips/ClipAnswerPanel';

// Referee video-clips library. Three regions:
//   1. Rule nav bar — current sport's rule articles, click narrows the library to one rule
//   2. Main player + decision panel — daily clip by default, swaps when a library card is picked
//   3. Right-side library list — published clips, optionally filtered by rule
// Stats cards in the top row are placeholders for now; they wire up with M6 (attempts) and
// M9 (daily-assignment + streak). M5 closes here on the playable shell; the WRITE side of
// the decision panel (submit + reveal) lands with M6 — buttons render but are disabled.
//
// Two view modes (?view=grid in the URL, YouTube's "watch" vs "channel videos" split):
//   'player' (default) — the daily/active clip playing, with a compact library list beside it.
//   'grid'   — clicking the "Library" heading swaps the player out for a full-width card grid
//              of every clip, no player, same infinite-scroll paging. Picking a card there drops
//              back into 'player' with that clip active. Kept in the URL (not component state) so
//              browser back from the grid returns to the player instead of leaving the page.

// mm:ss for the card duration badge. Returns "—:—" when the clip hasn't transcoded yet
// (duration_sec is set by the transcoding webhook). Single decimal place is overkill for
// a 30-second clip; whole seconds match the design's "0:32" notation.
function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—:—';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function RefereeClipsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?rule=<uuid> and ?tag=<uuid> drive the library filter — kept in the URL so a deep link or
  // browser-back restores the same view. `null` (no param) = no filter on that axis. The two
  // compose server-side (AND), so a clip must match both an active rule and an active tag.
  const ruleFilter = searchParams.get('rule');
  const tagFilter = searchParams.get('tag');
  // Derived straight from the URL (not useState) so the browser's own back/forward history
  // naturally toggles the grid open and closed, matching how ?rule/?tag already behave.
  const view: 'player' | 'grid' = searchParams.get('view') === 'grid' ? 'grid' : 'player';

  // The federation's managed tag vocabulary for this sport (the filter chips). Small + rarely
  // changing, so it rides the default long stale window.
  const tagsQuery = useClipTagsQuery();

  // Resolve the referee's sport slug so we can fetch its rule articles. The /sports/:slug/rules
  // endpoint is keyed by slug, not id; the sports catalogue is a small, cached lookup either way.
  // super_admin has no sport_id (this page isn't reachable for them via RoleHome anyway, but
  // belt-and-braces — the queries gracefully no-op with an empty list).
  const sportsQuery = useSportsQuery();
  const sportSlug = useMemo(
    () => sportsQuery.data?.find((s) => s.id === user?.sport_id)?.slug,
    [sportsQuery.data, user?.sport_id],
  );
  const rulesQuery = useRulesQuery(sportSlug);

  const clipsQuery = useRefereeClipsInfiniteQuery({
    ruleArticleId: ruleFilter ?? undefined,
    tagId: tagFilter ?? undefined,
  });
  // Flatten the loaded pages into one list for the library grid.
  const libraryClips = useMemo(() => clipsQuery.data?.pages.flat() ?? [], [clipsQuery.data]);
  // The REAL per-user daily assignment (GET /v1/me/daily) — the same query Home's hero reads, so
  // the two pages can never disagree on which clip is "today's" or whether it's been answered.
  // (This page used to read the old pre-M9 /clips/daily stub via a same-named hook in
  // hooks/use-clips.ts — "most recently published clip" — which is why a freshly uploaded clip
  // could briefly wear the DAILY badge here while Home showed the real assignment. That stub and
  // its hook have been removed; there is only one daily-clip source now.)
  const dailyQuery = useDailyClipQuery(true);

  // Active clip: null = "play the daily clip in the main area". When the user clicks a
  // library card, we set this to that clip's id and refetch its playback URL (the list
  // shape only has poster_url, not playback_url — playback is minted per-load with a 1h TTL).
  // Seeded from ?clip=<id> so a deep link (e.g. from the Bookmarked page) opens that clip in
  // the player on first render; the param is read once for the initial value, then state owns it.
  const [activeClipId, setActiveClipId] = useState<string | null>(() => searchParams.get('clip'));
  const activeClipQuery = useClipQuery(activeClipId ?? undefined);

  // Resolve the clip that should render in the main player. activeClipQuery has the
  // playback URL when set; otherwise the daily clip is the default. Both can be null
  // during initial load — the player block renders a spinner in that case.
  const activeClip: ClipPlayback | null =
    (activeClipId ? activeClipQuery.data : dailyQuery.data?.clip) ?? null;

  // Signed playback URL expired mid-session → refetch whichever query minted the active clip's URL
  // (the picked library clip, or the daily fallback) so the player gets a fresh 1h URL and resumes.
  const refetchActiveClip = () => {
    if (activeClipId) activeClipQuery.refetch();
    else dailyQuery.refetch();
  };

  // Merge one filter axis into the URL, preserving the other (rule + tag compose). Replace (not
  // push) so the pills don't pollute history — clicking through filters stays one "back" from where
  // the user came in. Clearing the active selection keeps the player aligned with the library
  // (otherwise the "active" card might be filtered out and the highlight would vanish weirdly).
  function setFilter(patch: { rule?: string | null; tag?: string | null }) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    // ?clip is a transient player deep-link, not a filter — drop it so it can't linger stale.
    next.delete('clip');
    setSearchParams(next, { replace: true });
    setActiveClipId(null);
  }

  const selectRule = (ruleId: string | null) => setFilter({ rule: ruleId });
  const selectTag = (tagId: string | null) => setFilter({ tag: tagId });

  // Grid view is a real navigation (like opening a channel's "Videos" tab), so it gets its own
  // history entry — pushed, not replaced — unlike the filter pills above. That makes the
  // browser/back-gesture close the grid and return to the player, which is the behaviour people
  // expect from a "back" press after drilling into a list.
  function openLibrary() {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'grid');
    setSearchParams(next);
  }

  function closeLibrary() {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next);
  }

  // Picking a card from the grid plays it, same as picking one from the sidebar list — and
  // drops back out of grid mode into the player, since that's the point of picking a video.
  function pickClip(id: string) {
    setActiveClipId(id);
    if (view === 'grid') closeLibrary();
  }

  const dailyId = dailyQuery.data?.clip?.id ?? null;

  return (
    <>
      <PageHeader
        title={t('clips.library.title')}
        sub={t('clips.library.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            {/* Filter + Search are placeholders for the design; both wire up later. */}
            <Button variant="default" size="md" disabled aria-label="Filter (coming soon)">
              <Icon.menu className="h-4 w-4" />
              {t('clips.library.filter')}
            </Button>
            <Button variant="default" size="md" disabled aria-label="Search clips (coming soon)">
              <Icon.search className="h-4 w-4" />
              {t('clips.library.search')}
            </Button>
          </div>
        }
      />

      <RuleNavBar
        rules={rulesQuery.data ?? []}
        loading={rulesQuery.isLoading}
        activeRuleId={ruleFilter}
        onSelect={selectRule}
      />

      {/* Managed-tag filter — only rendered once the federation has defined at least one tag, so an
          untagged library doesn't show an empty control. */}
      <TagFilterBar
        tags={tagsQuery.data ?? []}
        activeTagId={tagFilter}
        onSelect={selectTag}
      />

      {view === 'player' && <StatCardsRow />}

      {/* Changing the rule/tag filter now keeps the previous results on screen while the new ones
          load (placeholderData) — dim them so they read as being replaced, not as this filter's
          answer. Wrapped here rather than threaded through LibraryGrid/LibraryPanel, which are
          presentational and have no business knowing about query state. */}
      <div
        className={placeholderDim(clipsQuery.isPlaceholderData)}
        aria-busy={clipsQuery.isPlaceholderData || undefined}
      >
      {view === 'grid' ? (
        <LibraryGrid
          clips={libraryClips}
          loading={clipsQuery.isLoading}
          error={clipsQuery.isError}
          activeClipId={activeClip?.id ?? null}
          dailyClipId={dailyId}
          activeRuleLabel={
            ruleFilter ? rulesQuery.data?.find((r) => r.id === ruleFilter)?.article_number : null
          }
          onPick={pickClip}
          onBack={closeLibrary}
          hasNextPage={clipsQuery.hasNextPage}
          isFetchingNextPage={clipsQuery.isFetchingNextPage}
          onLoadMore={clipsQuery.fetchNextPage}
        />
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <ClipAnswerPanel
            clip={activeClip}
            isDaily={activeClip?.id === dailyId}
            // Only the query that actually feeds the main player counts as "loading" — otherwise a
            // library with nothing published would spin forever instead of saying so.
            loading={activeClipId ? activeClipQuery.isLoading : dailyQuery.isLoading}
            onSourceExpired={refetchActiveClip}
          />
          <LibraryPanel
            clips={libraryClips}
            loading={clipsQuery.isLoading}
            error={clipsQuery.isError}
            activeClipId={activeClip?.id ?? null}
            dailyClipId={dailyId}
            activeRuleLabel={
              ruleFilter ? rulesQuery.data?.find((r) => r.id === ruleFilter)?.article_number : null
            }
            onPick={pickClip}
            onExpand={openLibrary}
            hasNextPage={clipsQuery.hasNextPage}
            isFetchingNextPage={clipsQuery.isFetchingNextPage}
            onLoadMore={clipsQuery.fetchNextPage}
          />
        </div>
      )}
      </div>
    </>
  );
}

// ─── Rule navigation bar ────────────────────────────────────────────────────────

interface RuleNavBarProps {
  rules: RuleArticle[];
  loading: boolean;
  activeRuleId: string | null;
  onSelect: (ruleId: string | null) => void;
}

// Horizontal rule picker. "All clips" + one pill per rule article. Active pill is filled;
// the rest are tonal. The design's "Browse N rules" dropdown is intentionally deferred —
// a flat scrollable strip works for v1's 18-article rule-book; a dropdown becomes worth
// the affordance only once a sport has dozens of rules.
function RuleNavBar({ rules, loading, activeRuleId, onSelect }: RuleNavBarProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded border border-line bg-surface p-3 shadow-1">
      <div className="flex items-center gap-2 overflow-x-auto">
        <RulePill
          label={t('clips.library.allClips')}
          active={activeRuleId === null}
          onClick={() => onSelect(null)}
        />
        {loading && <Spinner className="h-4 w-4 text-muted" />}
        {rules.map((r) => (
          <RulePill
            key={r.id}
            label={`${r.article_number}  ${r.title}`}
            active={activeRuleId === r.id}
            onClick={() => onSelect(r.id)}
          />
        ))}
      </div>
    </div>
  );
}

function RulePill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex h-8 flex-none items-center gap-1.5 rounded-btn border px-3 text-xs font-medium transition-colors',
        active
          ? 'border-accent bg-accent text-accent-ink'
          : 'border-line-2 bg-surface text-ink-2 hover:bg-surface-2 hover:text-ink',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// ─── Tag filter bar ───────────────────────────────────────────────────────────────

interface TagFilterBarProps {
  tags: ClipTag[];
  activeTagId: string | null;
  onSelect: (tagId: string | null) => void;
}

// Managed-vocabulary tag filter — a chip strip below the rule bar. Single-select (one tag at a
// time, matching the rule filter + the single `tag_id` API param); clicking the active chip clears
// it. Renders nothing when the federation hasn't defined any tags, so an untagged sport shows no
// empty control.
function TagFilterBar({ tags, activeTagId, onSelect }: TagFilterBarProps) {
  const { t } = useTranslation();
  if (tags.length === 0) return null;
  return (
    <div className="mt-3 flex items-center gap-2 overflow-x-auto rounded border border-line bg-surface p-3 shadow-1">
      <span className="flex-none text-[11px] font-semibold uppercase tracking-wider text-muted">
        {t('clips.library.tagsLabel')}
      </span>
      {tags.map((t) => {
        const active = t.id === activeTagId;
        return (
          <button
            key={t.id}
            type="button"
            // Toggle: re-clicking the active tag clears the filter.
            onClick={() => onSelect(active ? null : t.id)}
            aria-pressed={active}
            // Colour from the tag's palette key — solid when the filter is active, outline otherwise.
            style={tagChipStyle(t.color, active)}
            className="inline-flex h-7 flex-none items-center gap-1 rounded-full border px-3 text-[11px] font-medium transition-colors"
          >
            {t.label}
            {active && <Icon.close className="h-3 w-3" />}
          </button>
        );
      })}
    </div>
  );
}

// ─── Stat cards ─────────────────────────────────────────────────────────────────

// The four stat cards. Three are live off the referee's mastery summary (Clips answered, Rule
// accuracy, Weakest area); Study streak comes from the M9 daily loop, on its own query — the same
// GET /v1/me/streak the Home card reads, so the two can never disagree about the streak.
// Both queries are role-gated — only referees / federation_admins have these (super_admin 403s).
function StatCardsRow() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const eligible = role === 'referee' || role === 'federation_admin';
  const { data, isLoading } = useRefereeStatsQuery(eligible);
  const { data: streak, isLoading: streakLoading } = useStreakQuery(eligible);

  // First load only (no cached data yet) → skeletons; a background refetch keeps the last values on
  // screen (the global FetchingBar is the cue), and isError-without-data falls through to "—".
  const loading = eligible && isLoading && !data;

  // Numeric figures roll to their value (AnimatedNumber); non-numeric states stay a plain em dash.
  const answered = data ? <AnimatedNumber value={data.clips_answered} /> : '—';
  const accuracy =
    data && data.clips_answered > 0 ? (
      <>
        <AnimatedNumber value={Math.round(data.rule_accuracy * 100)} />%
      </>
    ) : (
      '—'
    );
  const weakest = data?.weakest_rule ?? null;

  // Streak caption mirrors Home's wording so the same number never carries two different stories:
  // answered today = safe, a live streak = still to do, nothing yet = an invitation to start.
  const streakDays = streak?.current_streak ?? 0;
  const streakCaption = streak?.answered_today
    ? t('clips.library.stats.answeredToday')
    : streakDays > 0
      ? t('clips.library.stats.keepItToday')
      : t('clips.library.stats.startToday');

  return (
    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard
        label={t('clips.library.stats.clipsAnswered')}
        value={answered}
        caption={t('clips.library.stats.firstAttempt')}
        loading={loading}
      />
      <StatCard
        label={t('clips.library.stats.ruleAccuracy')}
        value={accuracy}
        caption={t('clips.library.stats.ofFirstAnswers')}
        loading={loading}
      />
      <StatCard
        label={t('clips.library.stats.weakestArea')}
        value={weakest ? t('common.ruleLabel', { number: weakest.article_number }) : '—'}
        caption={weakest ? weakest.title : t('clips.library.stats.noAttempts')}
        loading={loading}
      />
      <StatCard
        label={t('clips.library.stats.studyStreak')}
        value={streak ? <AnimatedNumber value={streakDays} /> : '—'}
        caption={streakCaption}
        loading={eligible && streakLoading && !streak}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  caption,
  loading = false,
}: {
  label: string;
  // ReactNode so a card can carry an animated figure (74 %) or a plain non-numeric state ("—").
  value: ReactNode;
  caption: string;
  loading?: boolean;
}) {
  return (
    <div className="rounded border border-line bg-surface p-4 shadow-1">
      <div className="text-[11px] uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-2 font-display text-3xl text-ink-3">
        {loading ? <Skeleton className="h-8 w-16" /> : value}
      </div>
      <div className="mt-1 truncate text-[11px] text-muted">{caption}</div>
    </div>
  );
}


// ─── Library panel (sidebar list, player view) ─────────────────────────────────

interface LibraryPanelProps {
  clips: ClipPublic[];
  loading: boolean;
  error: boolean;
  activeClipId: string | null;
  dailyClipId: string | null;
  activeRuleLabel: string | null | undefined;
  onPick: (id: string) => void;
  // Expands into the full-page grid (LibraryGrid) — wired to the "Library" heading itself,
  // the click target the user asked for ("when I click text library").
  onExpand: () => void;
  // Infinite-scroll paging: whether another page exists, whether one is in flight, and the
  // fetcher to pull it. The panel renders a sentinel that calls onLoadMore as it nears view.
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

function LibraryPanel({
  clips,
  loading,
  error,
  activeClipId,
  dailyClipId,
  activeRuleLabel,
  onPick,
  onExpand,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: LibraryPanelProps) {
  const { t } = useTranslation();
  // Fire the next-page fetch as the sentinel nears view; only armed when there's more to load and
  // no fetch is already running (avoids a burst of duplicate page requests while one is in flight).
  const sentinelRef = useInfiniteScroll(onLoadMore, hasNextPage && !isFetchingNextPage);

  return (
    <aside className="rounded border border-line bg-surface p-4 shadow-1">
      <div className="flex items-baseline justify-between">
        <button
          type="button"
          onClick={onExpand}
          className="group flex items-center gap-1 font-display text-lg text-ink transition-colors hover:text-accent"
        >
          {/* No aria-label here — the visible "Library" text is already the accessible name;
              an override would just diverge from what's on screen (and briefly collided with
              the unrelated "All clips" rule pill's own accessible name in tests). */}
          {t('clips.library.heading')}
          {activeRuleLabel ? ` · ${t('common.ruleLabel', { number: activeRuleLabel })}` : ''}
          <Icon.chevronRight className="h-4 w-4 text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />
        </button>
        <span className="text-xs text-muted">
          {/* A trailing "+" while more pages exist, so the count reads as "loaded so far", not total.
              The unit word is forced to its non-singular form whenever "+" shows (count: 2 is never
              actually shown, just used to pick the plural category) — matches the original's
              `clips.length === 1 && !hasNextPage ? '' : 's'` in every language, not just English. */}
          {clips.length}
          {hasNextPage ? '+' : ''} {t('clips.library.clipUnit', { count: hasNextPage ? 2 : clips.length })}
        </span>
      </div>

      <div className="mt-3 space-y-2">
        {/* First load only — card-shaped placeholders. A background refetch keeps the cached
            cards visible (loading is false when data already exists); the FetchingBar is the cue. */}
        {loading && (
          <>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[68px] w-full" />
            ))}
          </>
        )}

        {/* Only show the error line when there's nothing cached to fall back on — a failed
            background refetch keeps the last-good library on screen. */}
        {error && clips.length === 0 && (
          <div className="rounded border border-danger-tint bg-danger-tint px-3 py-2 text-xs text-danger">
            {t('clips.library.couldNotLoad')}
          </div>
        )}

        {!loading && !error && clips.length === 0 && (
          <div className="rounded border border-dashed border-line-2 bg-surface-2 px-3 py-6 text-center text-xs text-muted">
            {t('clips.library.noPublished')}
          </div>
        )}

        {clips.map((c) => (
          <LibraryCard
            key={c.id}
            clip={c}
            active={c.id === activeClipId}
            isDaily={c.id === dailyClipId}
            onClick={() => onPick(c.id)}
          />
        ))}

        {/* Sentinel + spinner for infinite scroll. The observer watches this element; when it nears
            the viewport the next page loads. A short skeleton row signals the in-flight fetch. */}
        {hasNextPage && (
          <div ref={sentinelRef} className="pt-1">
            {isFetchingNextPage ? (
              <Skeleton className="h-[68px] w-full" />
            ) : (
              <div className="grid place-items-center py-2 text-[11px] text-muted">
                {t('clips.library.scrollForMore')}
              </div>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function LibraryCard({
  clip,
  active,
  isDaily,
  onClick,
}: {
  clip: ClipPublic;
  active: boolean;
  isDaily: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'group flex w-full items-stretch gap-3 rounded border bg-surface p-2 text-left transition-colors',
        active
          ? 'border-accent shadow-1'
          : 'border-line-2 hover:border-line-3 hover:bg-surface-2',
      ].join(' ')}
    >
      <div className="relative aspect-video w-24 flex-none overflow-hidden rounded-sm bg-bg-2">
        {clip.poster_url ? (
          <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[10px] text-muted">
            {t('common.noPoster')}
          </div>
        )}
        <span className="absolute bottom-1 right-1 rounded-sm bg-ink/85 px-1.5 py-0.5 text-[10px] font-medium text-bg">
          {formatDuration(clip.duration_sec)}
        </span>
        {isDaily && (
          <span className="absolute left-1 top-1 rounded-sm bg-danger px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bg">
            {t('common.dailyBadge')}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        <div className="line-clamp-2 text-sm font-medium text-ink">{clip.title}</div>
        <div className="mt-1 text-[11px] text-muted">
          {clip.rule_article ? t('common.ruleLabel', { number: clip.rule_article.article_number }) : t('common.untagged')}
          {' · '}
          {timeAgo(clip.uploaded_at)}
        </div>
        {/* Managed tag chips (0–3). Rendered under the rule line so a card shows what it's tagged
            with; empty when the clip carries none. */}
        {clip.tags && clip.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {clip.tags.map((t) => (
              <span
                key={t.id}
                // Soft-tinted chip in the tag's own colour so a card's tags read at a glance.
                style={tagSoftStyle(t.color)}
                className="rounded-full px-2 py-0.5 text-[10px] font-medium"
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

// ─── Library grid (full-page "all videos" view, YouTube channel-style) ────────────

interface LibraryGridProps {
  clips: ClipPublic[];
  loading: boolean;
  error: boolean;
  activeClipId: string | null;
  dailyClipId: string | null;
  activeRuleLabel: string | null | undefined;
  onPick: (id: string) => void;
  onBack: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

// The "click Library → see every clip as a big card grid, click one → play it" view. Same data
// (clipsQuery) and the same infinite-scroll sentinel as the sidebar list — this is a different
// layout over the same clips, not a separate fetch.
function LibraryGrid({
  clips,
  loading,
  error,
  activeClipId,
  dailyClipId,
  activeRuleLabel,
  onPick,
  onBack,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: LibraryGridProps) {
  const { t } = useTranslation();
  const sentinelRef = useInfiniteScroll(onLoadMore, hasNextPage && !isFetchingNextPage);

  return (
    <div className="mt-5">
      <div className="flex items-baseline justify-between">
        <button
          type="button"
          onClick={onBack}
          className="group flex items-center gap-1.5 font-display text-lg text-ink transition-colors hover:text-accent"
        >
          <Icon.chevronLeft className="h-4 w-4 text-muted transition-transform group-hover:-translate-x-0.5 group-hover:text-accent" />
          {t('clips.library.heading')}
          {activeRuleLabel ? ` · ${t('common.ruleLabel', { number: activeRuleLabel })}` : ''}
        </button>
        <span className="text-xs text-muted">
          {clips.length}
          {hasNextPage ? '+' : ''} {t('clips.library.clipUnit', { count: hasNextPage ? 2 : clips.length })}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
        {loading &&
          Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-video w-full" />
          ))}

        {error && clips.length === 0 && (
          <div className="col-span-full rounded border border-danger-tint bg-danger-tint px-3 py-2 text-xs text-danger">
            {t('clips.library.couldNotLoad')}
          </div>
        )}

        {!loading && !error && clips.length === 0 && (
          <div className="col-span-full rounded border border-dashed border-line-2 bg-surface-2 px-3 py-10 text-center text-xs text-muted">
            {t('clips.library.noPublished')}
          </div>
        )}

        {clips.map((c) => (
          <GridCard
            key={c.id}
            clip={c}
            active={c.id === activeClipId}
            isDaily={c.id === dailyClipId}
            onClick={() => onPick(c.id)}
          />
        ))}
      </div>

      {/* Sentinel + spinner for infinite scroll, same pattern as the sidebar list. */}
      {hasNextPage && (
        <div ref={sentinelRef} className="pt-4">
          {isFetchingNextPage ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="aspect-video w-full" />
              ))}
            </div>
          ) : (
            <div className="grid place-items-center py-2 text-[11px] text-muted">
              {t('clips.library.scrollForMore')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// A YouTube-style "video card": thumbnail on top (full card width, 16:9), title + meta below —
// as opposed to LibraryCard's compact horizontal row, which fits the narrow player-view sidebar.
function GridCard({
  clip,
  active,
  isDaily,
  onClick,
}: {
  clip: ClipPublic;
  active: boolean;
  isDaily: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'group flex flex-col overflow-hidden rounded border bg-surface text-left transition-colors',
        active ? 'border-accent shadow-1' : 'border-line-2 hover:border-line-3 hover:bg-surface-2',
      ].join(' ')}
    >
      <div className="relative aspect-video w-full overflow-hidden bg-bg-2">
        {clip.poster_url ? (
          <img src={clip.poster_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full w-full place-items-center text-[11px] text-muted">
            {t('common.noPoster')}
          </div>
        )}
        <span className="absolute bottom-1.5 right-1.5 rounded-sm bg-ink/85 px-1.5 py-0.5 text-[11px] font-medium text-bg">
          {formatDuration(clip.duration_sec)}
        </span>
        {isDaily && (
          <span className="absolute left-1.5 top-1.5 rounded-sm bg-danger px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bg">
            {t('common.dailyBadge')}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 p-2.5">
        <div className="line-clamp-2 text-sm font-medium text-ink">{clip.title}</div>
        <div className="mt-1 text-[11px] text-muted">
          {clip.rule_article ? t('common.ruleLabel', { number: clip.rule_article.article_number }) : t('common.untagged')}
          {' · '}
          {timeAgo(clip.uploaded_at)}
        </div>
        {clip.tags && clip.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {clip.tags.map((t) => (
              <span
                key={t.id}
                style={tagSoftStyle(t.color)}
                className="rounded-full px-2 py-0.5 text-[10px] font-medium"
              >
                {t.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}
